import axios from 'axios'

const baseURL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'

export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
})

// Attach JWT to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// On 401, clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  },
)

// ─── Sites ───────────────────────────────────────────────────────────────────

export interface Site {
  id: string
  name: string
  url: string
  description: string
  site_type: 'portfolio' | 'app' | 'blog' | 'community'
  is_hub: 0 | 1
  index_now_key: string | null
  created_at: string
}

export const sitesApi = {
  getAll: () => api.get<Site[]>('/sites').then((r) => r.data),
  create: (data: Omit<Site, 'id' | 'created_at' | 'is_hub'>) =>
    api.post<Site>('/sites', data).then((r) => r.data),
  update: (id: string, data: Partial<Omit<Site, 'id' | 'created_at'>>) =>
    api.put<Site>(`/sites/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/sites/${id}`),
}

// ─── Keywords ────────────────────────────────────────────────────────────────

export interface Keyword {
  id: string
  site_id: string
  keyword: string
  target_url: string
  search_engine: 'google' | 'bing'
  location: string
  is_brand: 0 | 1
  current_rank: number | null
  previous_rank: number | null
  best_rank: number | null
  worst_rank: number | null
  added_date: string
}

export interface RankingHistory {
  id: string
  keyword_id: string
  rank: number | null
  search_engine: string
  checked_at: string
}

// A brand keyword joined with the site it belongs to, for the cross-site Brand Monitor view
export interface BrandKeyword extends Keyword {
  site_name: string
  site_url: string
}

export const keywordsApi = {
  getBySite: (siteId: string) =>
    api.get<Keyword[]>(`/keywords?site_id=${siteId}`).then((r) => r.data),
  getBrand: () =>
    api.get<BrandKeyword[]>('/keywords/brand').then((r) => r.data),
  create: (data: Omit<Keyword, 'id' | 'added_date' | 'current_rank' | 'previous_rank' | 'best_rank' | 'worst_rank'>) =>
    api.post<Keyword>('/keywords', data).then((r) => r.data),
  update: (id: string, data: Partial<Omit<Keyword, 'id' | 'site_id' | 'added_date'>>) =>
    api.put<Keyword>(`/keywords/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/keywords/${id}`),
  updateRanking: (id: string, rank: number | null, searchEngine: string) =>
    api.post<Keyword>(`/keywords/${id}/ranking`, { rank, search_engine: searchEngine }).then((r) => r.data),
  getHistory: (id: string) =>
    api.get<RankingHistory[]>(`/keywords/${id}/history`).then((r) => r.data),
  getLastChecked: (siteId: string) =>
    api.get<{ last_checked: string | null }>(`/keywords/last-checked?site_id=${siteId}`).then((r) => r.data.last_checked),
  suggestFromSite: (url: string) =>
    api.get<{ keywords: string[] }>(`/analyzer/suggest-keywords?url=${encodeURIComponent(url)}`).then((r) => r.data),
}

// ─── Submissions ─────────────────────────────────────────────────────────────

export interface Submission {
  id: string
  site_id: string
  platform: string
  category: string
  submitted_url: string
  status: 'pending' | 'submitted' | 'verified' | 'failed'
  submitted_at: string | null
  verified_at: string | null
  notes: string | null
  automated: 0 | 1
  created_at: string
}

export const submissionsApi = {
  getBySite: (siteId: string) =>
    api.get<Submission[]>(`/submissions?site_id=${siteId}`).then((r) => r.data),
  create: (data: Omit<Submission, 'id' | 'created_at'>) =>
    api.post<Submission>('/submissions', data).then((r) => r.data),
  update: (id: string, data: Partial<Omit<Submission, 'id' | 'site_id' | 'created_at'>>) =>
    api.put<Submission>(`/submissions/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/submissions/${id}`),
}

// ─── Backlinks ───────────────────────────────────────────────────────────────

export interface Backlink {
  id: string
  site_id: string
  source_url: string
  target_url: string
  anchor_text: string
  type: string
  rel: 'dofollow' | 'nofollow' | 'ugc' | 'sponsored'
  status: 'active' | 'broken' | 'redirected' | 'pending'
  is_hub_link: 0 | 1
  last_checked: string | null
  notes: string | null
  added_date: string
}

export interface HubStatus {
  site_id: string
  site_name: string
  site_url: string
  hub_link_count: number
  active_hub_link_count: number
}

// A single hub-link backlink awaiting confirmation. site_name is a best-effort match
// against a known spoke's domain (via source_url) and may be null for external sources.
export interface PendingHubLink {
  id: string
  source_url: string
  target_url: string
  anchor_text: string
  status: Backlink['status']
  added_date: string
  site_name: string | null
}

export const backlinksApi = {
  getBySite: (siteId: string) =>
    api.get<Backlink[]>(`/backlinks?site_id=${siteId}`).then((r) => r.data),
  getHubStatus: () => api.get<HubStatus[]>('/backlinks/hub-status').then((r) => r.data),
  getPendingHubLinks: () => api.get<PendingHubLink[]>('/backlinks/pending-hub-links').then((r) => r.data),
  create: (data: Omit<Backlink, 'id' | 'added_date'>) =>
    api.post<Backlink>('/backlinks', data).then((r) => r.data),
  update: (id: string, data: Partial<Omit<Backlink, 'id' | 'site_id' | 'added_date'>>) =>
    api.put<Backlink>(`/backlinks/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/backlinks/${id}`),
}

// ─── Content Calendar ────────────────────────────────────────────────────────
// Content items are brand-level (span all sites); distribution channels track
// per-platform syndication (LinkedIn, Ministry of Testing, dev.to, ...).

export interface ContentItem {
  id: string
  origin_site_id: string | null
  title: string
  target_keyword: string | null
  status: 'idea' | 'drafting' | 'published'
  due_date: string | null
  notes: string | null
  created_at: string
}

export interface ContentChannel {
  id: string
  content_id: string
  channel: string
  url: string | null
  status: 'pending' | 'published'
  published_at: string | null
}

export const contentApi = {
  getAll: (originSiteId?: string) =>
    api
      .get<ContentItem[]>(originSiteId ? `/content?origin_site_id=${originSiteId}` : '/content')
      .then((r) => r.data),
  create: (data: Omit<ContentItem, 'id' | 'created_at'>) =>
    api.post<ContentItem>('/content', data).then((r) => r.data),
  update: (id: string, data: Partial<Omit<ContentItem, 'id' | 'created_at'>>) =>
    api.put<ContentItem>(`/content/${id}`, data).then((r) => r.data),
  delete: (id: string) => api.delete(`/content/${id}`),
  getChannels: (contentId: string) =>
    api.get<ContentChannel[]>(`/content/${contentId}/channels`).then((r) => r.data),
  addChannel: (contentId: string, data: Omit<ContentChannel, 'id' | 'content_id'>) =>
    api.post<ContentChannel>(`/content/${contentId}/channels`, data).then((r) => r.data),
  updateChannel: (channelId: string, data: Partial<Omit<ContentChannel, 'id' | 'content_id'>>) =>
    api.put<ContentChannel>(`/content/channels/${channelId}`, data).then((r) => r.data),
  deleteChannel: (channelId: string) => api.delete(`/content/channels/${channelId}`),
}

// ─── GitHub Repos ────────────────────────────────────────────────────────────

export interface GithubRepo {
  id: string
  repo_name: string
  repo_url: string
  last_reviewed: string | null
  created_at: string
}

export interface ChecklistItem {
  id: string
  repo_id: string
  category: string
  label: string
  checked: 0 | 1
}

export const githubApi = {
  getAll: () => api.get<GithubRepo[]>('/github/repos').then((r) => r.data),
  createRepo: (data: Omit<GithubRepo, 'id' | 'created_at'>) =>
    api.post<GithubRepo>('/github/repos', data).then((r) => r.data),
  updateRepo: (id: string, data: Partial<Omit<GithubRepo, 'id' | 'created_at'>>) =>
    api.put<GithubRepo>(`/github/repos/${id}`, data).then((r) => r.data),
  deleteRepo: (id: string) => api.delete(`/github/repos/${id}`),
  getChecklist: (repoId: string) =>
    api.get<ChecklistItem[]>(`/github/repos/${repoId}/checklist`).then((r) => r.data),
  updateChecklistItem: (itemId: string, checked: boolean) =>
    api.put<ChecklistItem>(`/github/checklist/${itemId}`, { checked }).then((r) => r.data),
}

// ─── Settings ────────────────────────────────────────────────────────────────

export type AppSettings = Record<string, string>

export const settingsApi = {
  get: () => api.get<AppSettings>('/settings').then((r) => r.data),
  update: (data: Partial<AppSettings>) =>
    api.put<AppSettings>('/settings', data).then((r) => r.data),
}
